import { useMemo, useState } from 'react';
import {
  RiArrowRightLine,
  RiFocus3Line,
  RiNodeTree,
  RiPaletteLine,
  RiShareForwardLine,
  RiSparklingLine,
} from 'react-icons/ri';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useCanvasStore } from '../canvas/canvasStore';
import { useStore } from '../state/store';
import { labelForType } from './icons';
import {
  adjacencyMatrix,
  bridgeEdges,
  buildGraphModel,
  communities,
  communityColor,
  criticalNodes,
  graphSummary,
  shortestPath,
} from '../analysis/graph';
import { cn } from '@/lib/utils';

const NOTE_COLOR_BY_COMMUNITY = ['blue', 'purple', 'green', 'red', 'yellow', 'orange', 'default'] as const;

export function GraphPanel() {
  const entities = useStore(state => state.entities);
  const relations = useStore(state => state.relations);
  const controller = useCanvasStore(state => state.controller);
  const revision = useCanvasStore(state => state.revision);
  const toast = useStore(state => state.toast);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [hover, setHover] = useState<{ row: number; col: number } | null>(null);

  const graph = useMemo(() => buildGraphModel(entities, relations), [entities, relations]);
  const summary = useMemo(() => graphSummary(graph), [graph]);
  const critical = useMemo(() => criticalNodes(graph, 10), [graph]);
  const comm = useMemo(() => communities(graph), [graph]);
  const bridges = useMemo(() => bridgeEdges(graph), [graph]);
  const matrix = useMemo(() => adjacencyMatrix(graph), [graph]);
  const path = useMemo(
    () => (from && to ? shortestPath(graph, from, to) : null),
    [graph, from, to]
  );

  const labelOf = (id: string) => graph.nodes.find(n => n.id === id)?.label ?? id.slice(0, 8);
  const maxScore = critical[0]?.score || 1;
  const maxBetween = Math.max(1, ...critical.map(c => c.betweenness));

  // 热力图只取度数最高的若干节点，保证可读
  const heatNodes = useMemo(() => {
    const degrees = new Map<string, number>();
    for (const edge of graph.edges) {
      degrees.set(edge.source, (degrees.get(edge.source) ?? 0) + 1);
      degrees.set(edge.target, (degrees.get(edge.target) ?? 0) + 1);
    }
    return [...graph.nodes]
      .sort((a, b) => (degrees.get(b.id) ?? 0) - (degrees.get(a.id) ?? 0))
      .slice(0, 14);
  }, [graph]);

  const heatIndex = useMemo(() => {
    const map = new Map<string, number>();
    matrix.nodes.forEach((n, i) => map.set(n.id, i));
    return map;
  }, [matrix]);

  const colorByCommunity = () => {
    if (!controller) return;
    controller.pushHistory();
    let painted = 0;
    const wallNodes = controller.surface.all().filter(el => el.refKind === 'entity' && el.refId);
    for (const element of wallNodes) {
      const community = comm.get(element.refId as string);
      if (community === undefined) continue;
      const color = NOTE_COLOR_BY_COMMUNITY[community % NOTE_COLOR_BY_COMMUNITY.length];
      controller.surface.update(element.id, {
        props: { ...(element.props as Record<string, unknown>), color },
      });
      painted += 1;
    }
    controller.requestRender();
    useCanvasStore.getState().bump();
    toast(painted ? 'success' : 'info', painted ? `已按社群为 ${painted} 张卡片着色` : '墙面上没有对应卡片');
  };

  const drawAllRopes = () => {
    if (!controller) return;
    const wallByRef = new Map<string, string>();
    for (const element of controller.surface.all()) {
      if (element.refKind && element.refId) wallByRef.set(element.refId, element.id);
    }
    controller.pushHistory();
    let drawn = 0;
    for (const edge of graph.edges) {
      const a = wallByRef.get(edge.source);
      const b = wallByRef.get(edge.target);
      if (!a || !b) continue;
      const exists = controller.surface.all().some(el => {
        if (el.type !== 'rope') return false;
        const props = el.props as { source?: { id?: string }; target?: { id?: string } };
        return (
          (props.source?.id === a && props.target?.id === b) ||
          (props.source?.id === b && props.target?.id === a)
        );
      });
      if (exists) continue;
      controller.addRope({ id: a, anchor: [0.5, 0] }, { id: b, anchor: [0.5, 0] }, edge.label ?? edge.type);
      drawn += 1;
    }
    controller.requestRender();
    useCanvasStore.getState().bump();
    toast(drawn ? 'success' : 'info', drawn ? `已在墙面补出 ${drawn} 条关系红线` : '关系已全部上墙');
  };

  if (graph.nodes.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <span className="label-caps">图谱分析</span>
        <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
          尚无线索可分析。先在「检索」中采集，或在「线索」中登记关系。
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex items-center gap-1.5">
        <RiNodeTree className="size-3.5 text-[var(--color-lead)]" />
        <span className="text-[12px] font-semibold">图谱分析</span>
        <Badge variant="muted" className="ml-auto">
          {summary.nodeCount} 节点 / {summary.edgeCount} 边
        </Badge>
      </div>

      {/* 网络概况 */}
      <div className="grid grid-cols-3 gap-1.5">
        <Metric label="密度" value={summary.density.toFixed(3)} />
        <Metric label="平均度" value={summary.averageDegree.toFixed(2)} />
        <Metric label="社群" value={String(summary.communityCount)} />
        <Metric label="连通分量" value={String(summary.componentCount)} warn={summary.componentCount > 1} />
        <Metric label="割点" value={String(summary.articulationCount)} warn={summary.articulationCount > 0} />
        <Metric label="孤立" value={String(summary.isolateCount)} warn={summary.isolateCount > 0} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Button size="xs" variant="outline" onClick={colorByCommunity}>
          <RiPaletteLine className="size-3" />
          按社群着色
        </Button>
        <Button size="xs" variant="outline" onClick={drawAllRopes}>
          <RiShareForwardLine className="size-3" />
          关系全部上墙
        </Button>
      </div>

      {/* 关键节点 */}
      <div className="flex flex-col gap-1.5">
        <span className="label-caps">关键节点（度 · 介数 · 割点）</span>
        <div className="flex flex-col gap-1">
          {critical.map(node => (
            <button
              key={node.id}
              type="button"
              onClick={() => {
                if (!controller) return;
                const element = controller.surface
                  .all()
                  .find(el => el.refKind === 'entity' && el.refId === node.id);
                if (element) {
                  controller.selection.set([element.id]);
                  controller.viewport.setCenter([element.x + element.w / 2, element.y + element.h / 2]);
                  controller.requestRender();
                  useCanvasStore.getState().bump();
                } else {
                  toast('info', `${node.label} 尚未上墙`);
                }
              }}
              className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-border bg-card px-2 py-1.5 text-left transition-colors hover:border-primary/40 hover:bg-primary/4"
            >
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: communityColor(node.community) }}
                title={`社群 ${node.community + 1}`}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[11.5px] font-medium">{node.label}</span>
                <span className="mt-0.5 block h-1 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary/60"
                    style={{ width: `${Math.round((node.score / maxScore) * 100)}%` }}
                  />
                </span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block font-mono text-[10px] text-muted-foreground">
                  度 {node.degree} · 介 {node.betweenness.toFixed(1)}
                </span>
                {node.isArticulation && (
                  <Badge variant="cord" className="mt-0.5">
                    割点
                  </Badge>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 社群分布 */}
      <div className="flex flex-col gap-1.5">
        <span className="label-caps">社群分布</span>
        <div className="flex flex-wrap gap-1">
          {[...new Set([...comm.values()])]
            .sort((a, b) => a - b)
            .map(community => {
              const members = graph.nodes.filter(n => comm.get(n.id) === community);
              return (
                <span
                  key={community}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-[3px] text-[10px]"
                >
                  <span className="size-2 rounded-full" style={{ background: communityColor(community) }} />
                  社群 {community + 1}
                  <span className="font-mono text-muted-foreground">{members.length}</span>
                </span>
              );
            })}
        </div>
      </div>

      {/* 最短路径 */}
      <div className="flex flex-col gap-1.5">
        <span className="label-caps">两点关联路径</span>
        <div className="flex items-center gap-1">
          <Select value={from} onValueChange={setFrom}>
            <SelectTrigger className="h-7 text-[11px]">
              <SelectValue placeholder="起点" />
            </SelectTrigger>
            <SelectContent>
              {graph.nodes.slice(0, 80).map(n => (
                <SelectItem key={n.id} value={n.id}>
                  {n.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <RiArrowRightLine className="size-3 shrink-0 text-muted-foreground" />
          <Select value={to} onValueChange={setTo}>
            <SelectTrigger className="h-7 text-[11px]">
              <SelectValue placeholder="终点" />
            </SelectTrigger>
            <SelectContent>
              {graph.nodes.slice(0, 80).map(n => (
                <SelectItem key={n.id} value={n.id}>
                  {n.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {from && to && (
          <div className="rounded-[var(--radius-sm)] border border-border bg-muted/40 p-2 text-[10.5px]">
            {path ? (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground">跳数 {path.length - 1}：</span>
                {path.map((id, index) => (
                  <span key={id} className="flex items-center gap-1">
                    {index > 0 && <RiArrowRightLine className="size-2.5 text-muted-foreground" />}
                    <span className="rounded bg-card px-1.5 py-0.5">{labelOf(id)}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-[var(--color-cord)]">两点之间没有已知路径（情报缺口）</span>
            )}
          </div>
        )}
      </div>

      {/* 关联矩阵 */}
      <div className="flex flex-col gap-1.5">
        <span className="label-caps">关联矩阵（前 {heatNodes.length} 个高频节点）</span>
        <div className="overflow-x-auto">
          <div
            className="grid gap-px"
            style={{ gridTemplateColumns: `84px repeat(${heatNodes.length}, 14px)` }}
            onMouseLeave={() => setHover(null)}
          >
            <span />
            {heatNodes.map((node, col) => (
              <span
                key={`h-${node.id}`}
                title={node.label}
                className={cn(
                  'text-center text-[8px] text-muted-foreground',
                  hover?.col === col && 'text-foreground'
                )}
              >
                {col + 1}
              </span>
            ))}

            {heatNodes.map((rowNode, row) => (
              <span key={`row-${rowNode.id}`} className="contents">
                <span
                  className={cn(
                    'truncate pr-1 text-right text-[9.5px] text-muted-foreground',
                    hover?.row === row && 'text-foreground'
                  )}
                  title={rowNode.label}
                >
                  {row + 1} {rowNode.label}
                </span>
                {heatNodes.map((colNode, col) => {
                  const a = heatIndex.get(rowNode.id);
                  const b = heatIndex.get(colNode.id);
                  const value = a !== undefined && b !== undefined ? matrix.matrix[a][b] : 0;
                  const intensity = value === 0 ? 0 : Math.min(1, value / 120);
                  const isFocus = hover && (hover.row === row || hover.col === col);
                  return (
                    <span
                      key={`${row}-${col}`}
                      title={`${rowNode.label} × ${colNode.label}：强度 ${value}`}
                      onMouseEnter={() => setHover({ row, col })}
                      className={cn('block h-3.5 w-3.5 rounded-[2px]', isFocus && 'ring-1 ring-primary/50')}
                      style={{
                        background:
                          row === col
                            ? 'rgba(0,0,0,0.06)'
                            : value === 0
                              ? 'rgba(0,0,0,0.03)'
                              : `rgba(30, 58, 95, ${0.18 + intensity * 0.72})`,
                      }}
                    />
                  );
                })}
              </span>
            ))}
          </div>
        </div>
        <span className="text-[9.5px] text-muted-foreground">
          行列为节点编号（左侧名称），色深=关系强度之和；对角线不含自环。
        </span>
      </div>

      {/* 桥接边 */}
      {bridges.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="label-caps">桥接关系（去掉即断裂，{bridges.length}）</span>
          <div className="flex flex-col gap-1">
            {bridges.slice(0, 8).map(edge => (
              <div
                key={edge.id}
                className="flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border bg-card px-2 py-1.5 text-[10.5px]"
              >
                <RiFocus3Line className="size-3 shrink-0 text-[var(--color-cord)]" />
                <span className="truncate">{labelOf(edge.source)}</span>
                <Badge variant="cord" className="shrink-0">
                  {edge.label ?? edge.type}
                </Badge>
                <span className="truncate">{labelOf(edge.target)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1.5 border-t border-dashed border-border pt-2.5 text-[10px] text-muted-foreground">
        <RiSparklingLine className="size-3" />
        介数中心度基于 Brandes 算法（无向无权），社群为标签传播结果。
      </div>
    </div>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={cn('rounded-[var(--radius-sm)] border border-border bg-card px-2 py-1.5', warn && 'border-[var(--color-cord)]/40')}>
      <div className={cn('font-mono text-[12px] font-semibold', warn && 'text-[var(--color-cord)]')}>{value}</div>
      <div className="text-[9.5px] text-muted-foreground">{label}</div>
    </div>
  );
}

export { labelForType };
export type { GNode as GraphNode } from '../analysis/graph';